(() => {
  const openers = new WeakMap();
  const productTitleSelector = '.design-dialog > h2, .product-details > h1';
  const titleWidths = new WeakMap();
  const fitProductTitle = title => {
    const width = title.getBoundingClientRect().width;
    if (!width) return;
    title.style.removeProperty('--product-title-size');
    title.style.whiteSpace = 'nowrap';
    const size = parseFloat(getComputedStyle(title).fontSize);
    const minimum = 1.125 * parseFloat(getComputedStyle(document.documentElement).fontSize);
    const range = document.createRange();
    range.selectNodeContents(title);
    const textWidth = range.getBoundingClientRect().width;
    const fitted = Math.min(size, size * Math.max(0, width - 2) / Math.max(1, textWidth));
    title.style.setProperty('--product-title-size', `${Math.max(minimum, fitted).toFixed(2)}px`);
    title.style.whiteSpace = fitted >= minimum ? 'nowrap' : 'normal';
    titleWidths.set(title, width);
  };
  const titleObserver = 'ResizeObserver' in window ? new ResizeObserver(entries => {
    entries.forEach(({ target }) => {
      const width = target.getBoundingClientRect().width;
      if (width !== titleWidths.get(target)) fitProductTitle(target);
    });
  }) : null;
  const initProductTitles = (root = document) => {
    root.querySelectorAll(productTitleSelector).forEach(title => {
      fitProductTitle(title);
      titleObserver?.observe(title);
    });
  };
  const refitProductTitles = () => document.querySelectorAll(productTitleSelector).forEach(fitProductTitle);
  window.addEventListener('resize', refitProductTitles);
  if (document.fonts) document.fonts.ready.then(refitProductTitles);
  const warmImages = root => {
    root.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
  };
  const modelObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      warmImages(entry.target);
      modelObserver.unobserve(entry.target);
    });
  }, { rootMargin: '240px' }) : null;
  const setModelActive = (media, active) => {
    const button = media.querySelector('[data-model-toggle]');
    const stage = media.querySelector('[data-model-stage-toggle]');
    media.dataset.modelActive = String(active);
    button?.setAttribute('aria-pressed', String(active));
    stage?.setAttribute('aria-pressed', String(active));
    if (button) {
      button.querySelector('span').textContent = active ? button.dataset.garmentLabel : button.dataset.modelLabel;
      if (stage) {
        const title = media.querySelector('.garment-image')?.alt || '';
        stage.setAttribute('aria-label', `${active ? button.dataset.garmentLabel : button.dataset.modelLabel}: ${title}`);
      }
    }
  };
  const initModelPreviews = (root = document) => {
    root.querySelectorAll('[data-model-media]').forEach(media => {
      if (media.dataset.modelInitialized) return;
      media.dataset.modelInitialized = 'true';
      media.dataset.modelReady = 'false';
      const image = media.querySelector('.model-image');
      const button = media.querySelector('[data-model-toggle]');
      const stage = media.querySelector('[data-model-stage-toggle]');
      if (!image || !button) return;
      setModelActive(media, false);
      button.hidden = false;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (stage) {
        stage.disabled = true;
        stage.setAttribute('aria-busy', 'true');
      }
      const ready = async () => {
        if (!image.complete || !image.naturalWidth) return;
        try { await image.decode(); } catch { if (!image.naturalWidth) return; }
        media.dataset.modelReady = 'true';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (stage) {
          stage.disabled = false;
          stage.removeAttribute('aria-busy');
        }
      };
      image.addEventListener('load', ready);
      image.addEventListener('error', () => {
        media.dataset.modelReady = 'false';
        setModelActive(media, false);
        button.hidden = true;
        button.disabled = true;
        if (stage) {
          stage.disabled = true;
          stage.removeAttribute('aria-busy');
        }
      });
      ready();
      if (modelObserver) modelObserver.observe(media);
      else warmImages(media);
    });
  };
  const openGallery = gallery => {
    if (!(gallery instanceof HTMLDetailsElement)) return;
    gallery.open = true;
    warmImages(gallery);
    initModelPreviews(gallery);
  };
  const openTargetGallery = () => {
    if (!window.location.hash.startsWith('#gallery-')) return;
    openGallery(document.getElementById(window.location.hash.slice(1)));
  };
  const normalize = value => value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const filterCatalogue = (catalogue, query) => {
    const tokens = normalize(query).split(' ').filter(Boolean);
    let count = 0;
    catalogue.querySelectorAll('[data-design-card]').forEach(card => {
      const category = card.dataset.designCategory;
      const aliases = /tees/i.test(category) ? 'shirt shirts tshirt tshirts t shirt' : /headwear/i.test(category) ? 'hat hats beanie beanies' : /hoodies/i.test(category) ? 'hoodie sweatshirt sweatshirts' : 'jersey';
      const text = normalize(`${card.dataset.designTitle} ${category} ${aliases}`);
      const match = tokens.every(token => text.includes(token));
      card.hidden = !match;
      if (match) count += 1;
    });
    catalogue.querySelectorAll('.design-gallery').forEach(gallery => {
      const matchingCards = [...gallery.querySelectorAll('[data-design-card]')].some(card => !card.hidden);
      gallery.hidden = tokens.length > 0 && !matchingCards;
      if (tokens.length > 0 && matchingCards) openGallery(gallery);
    });
    const directory = catalogue.querySelector('.category-directory');
    if (directory) directory.hidden = tokens.length > 0;
    const status = catalogue.querySelector('[data-catalogue-count]');
    if (status) {
      const template = count === 1 ? status.dataset.countSingular : status.dataset.countTemplate;
      status.textContent = template.replace('__COUNT__', String(count));
    }
    const empty = catalogue.querySelector('[data-catalogue-empty]');
    if (empty) empty.hidden = count !== 0;
  };
  const initCatalogues = (root = document) => {
    root.querySelectorAll('[data-design-catalogue]').forEach(catalogue => {
      const input = catalogue.querySelector('[data-catalogue-filter] input[name="q"]');
      if (!input) return;
      input.value = new URLSearchParams(window.location.search).get('q') || '';
      filterCatalogue(catalogue, input.value);
    });
  };
  const init = (root = document) => {
    initProductTitles(root);
    initModelPreviews(root);
    initCatalogues(root);
    openTargetGallery();
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('hashchange', openTargetGallery);
  document.addEventListener('toggle', event => {
    if (event.target.matches('.design-gallery') && event.target.open) {
      warmImages(event.target);
      initModelPreviews(event.target);
    }
  }, true);
  document.addEventListener('pointerover', event => {
    const media = event.target.closest('[data-model-media]');
    if (media) warmImages(media);
  });
  document.addEventListener('input', event => {
    const form = event.target.closest('[data-catalogue-filter]');
    if (form) filterCatalogue(form.closest('[data-design-catalogue]'), event.target.value);
  });
  document.addEventListener('submit', event => {
    if (!event.target.matches('[data-catalogue-filter]')) return;
    event.preventDefault();
    const query = event.target.querySelector('input[name="q"]').value;
    filterCatalogue(event.target.closest('[data-design-catalogue]'), query);
    const url = new URL(window.location.href);
    if (query) url.searchParams.set('q', query);
    else url.searchParams.delete('q');
    window.history.replaceState(null, '', url);
  });
  const closeMenus = (except) => {
    document.querySelectorAll('.site-menu[open]').forEach((menu) => {
      if (menu !== except) menu.open = false;
    });
  };
  document.addEventListener('click', (event) => {
    const clear = event.target.closest('[data-catalogue-clear]');
    if (clear) {
      const catalogue = clear.closest('[data-design-catalogue]');
      const input = catalogue.querySelector('[data-catalogue-filter] input[name="q"]');
      input.value = '';
      filterCatalogue(catalogue, '');
      const url = new URL(window.location.href);
      url.searchParams.delete('q');
      window.history.replaceState(null, '', url);
      input.focus();
      return;
    }
    const modelToggle = event.target.closest('[data-model-toggle], [data-model-stage-toggle]');
    if (modelToggle) {
      event.preventDefault();
      const media = modelToggle.closest('[data-model-media]');
      if (!media || media.dataset.modelReady !== 'true') return;
      setModelActive(media, media.dataset.modelActive !== 'true');
      return;
    }
    const anchor = event.target.closest('a[href]');
    if (anchor) {
      const url = new URL(anchor.href, window.location.href);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname && url.hash.startsWith('#gallery-')) {
        openGallery(document.getElementById(url.hash.slice(1)));
      }
    }
    const designLink = event.target.closest('a[href^="#design-"]');
    if (designLink) {
      const dialog = document.getElementById(designLink.getAttribute('href').slice(1));
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        openers.set(dialog, designLink);
        dialog.showModal();
        initProductTitles(dialog);
        warmImages(dialog);
      }
    }
    const dialog = event.target.closest('.design-dialog');
    if (dialog && (event.target.closest('[data-dialog-close]') || event.target.closest('a[href^="#newsletter"]'))) dialog.close();
    const menu = event.target.closest('.site-menu');
    closeMenus(menu);
    if (menu && event.target.closest('.menu-panel a')) menu.open = false;
  });
  document.addEventListener('close', (event) => {
    if (!event.target.matches('.design-dialog')) return;
    openers.get(event.target)?.focus({ preventScroll: true });
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = document.querySelector('.site-menu[open]');
    if (!menu) return;
    menu.open = false;
    menu.querySelector('summary')?.focus();
  });
})();
